import { css } from '@emotion/react';
import { type FC, type HTMLAttributes, type ReactNode } from 'react';

import { ThemeTokens } from '../shared/styles';
import { Heading, Text } from '../Typography';

const { media } = ThemeTokens;

export type SectionHeaderTone = 'default' | 'inverse';

export type SectionHeaderProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  /** Short label above the title, for example "How LandTrust works". */
  eyebrow?: ReactNode;
  /** The section heading. */
  title: ReactNode;
  /** Supporting copy under the title. */
  description?: ReactNode;
  /** Optional link or button shown beside the heading on wide screens. */
  action?: ReactNode;
  /** Heading element. Defaults to `h2`. */
  headingLevel?: 'h1' | 'h2' | 'h3';
  /** Use `inverse` on dark surfaces. */
  tone?: SectionHeaderTone;
};

const rootStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-4) var(--spacing-8);
`;

const copyStyles = css`
  display: flex;
  flex: 1 1 320px;
  flex-direction: column;
  gap: var(--spacing-3);
  max-width: 720px;
`;

const eyebrowStyles = css`
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const descriptionStyles = css`
  && {
    margin: 0;
    font-size: 17px;
    line-height: 1.65;
  }

  ${media.md} {
    && {
      font-size: 18px;
    }
  }
`;

const toneColors: Record<
  SectionHeaderTone,
  { eyebrow: string; title: string; description: string }
> = {
  default: {
    eyebrow: 'var(--color-primary-500)',
    title: 'var(--color-primary-800)',
    description: 'var(--color-neutral-800)',
  },
  inverse: {
    eyebrow: 'var(--color-primary-200)',
    title: 'var(--color-base-white)',
    description: 'var(--color-primary-100)',
  },
};

const HEADING_SIZE = { h1: 'lg', h2: 'md', h3: 'sm' } as const;

/**
 * Eyebrow, title, description and an optional action for the top of a page
 * section.
 */
const SectionHeader: FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  action,
  headingLevel = 'h2',
  tone = 'default',
  className,
  ...rest
}) => {
  const colors = toneColors[tone];

  return (
    <header {...rest} className={className} css={rootStyles}>
      <div css={copyStyles}>
        {eyebrow && (
          <Text
            as="p"
            size="sm"
            fontWeight="bold"
            color={colors.eyebrow}
            css={eyebrowStyles}
          >
            {eyebrow}
          </Text>
        )}
        <Heading
          as={headingLevel}
          fontWeight="extrabold"
          size={HEADING_SIZE[headingLevel]}
          color={colors.title}
          textWrap="balance"
        >
          {title}
        </Heading>
        {description && (
          <Text as="p" color={colors.description} css={descriptionStyles}>
            {description}
          </Text>
        )}
      </div>
      {action}
    </header>
  );
};

SectionHeader.displayName = 'SectionHeader';

export default SectionHeader;
